import React from "react";
import { useEffectiveTheme } from "../../hooks/useEffectiveTheme";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { cn } from "../../lib/utils";

/**
 * Preview card that applies a theme and persists the selection.
 * @param {Object} props
 * @param {string} props.themeId
 * @param {string} props.label
 * @param {string} props.currentTheme
 * @param {function(string): void} props.setTheme
 * @param {function(Object): void} props.onUpdateSettings
 * @returns {JSX.Element}
 */
function ThemeCard({ themeId, label, currentTheme, setTheme, onUpdateSettings }) {
  const previewClass = useEffectiveTheme(themeId);
  const isSelected = currentTheme === themeId;

  const handleClick = () => {
    setTheme(themeId);
    onUpdateSettings({ theme: themeId });
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={cn(
        previewClass,
        "group relative z-0 block w-full origin-center text-left transition-transform duration-200 ease-out",
        "hover:z-10 hover:scale-[1.03]",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background"
      )}
    >
      <Card
        className={cn(
          "h-full border-2 transition-[border-color,box-shadow] duration-500",
          "group-hover:border-accent/50 group-hover:shadow-md",
          isSelected ? "border-accent bg-accent/10" : "border-ui-border"
        )}
      >
        <CardHeader className="space-y-1 pb-2">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base text-text-primary">{label}</CardTitle>
            {isSelected ? <span className="text-sm text-accent" aria-hidden>✓</span> : null}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="space-y-2">
            <div className="h-12 rounded border border-border bg-primary" />
            <div className="h-8 rounded border border-border bg-secondary" />
            <div className="h-6 rounded border border-border bg-tertiary" />
            <div className="flex gap-2">
              <div className="h-4 w-4 rounded bg-accent" />
              <div className="h-4 w-4 rounded bg-text-primary" />
              <div className="h-4 w-4 rounded bg-text-secondary" />
            </div>
          </div>
        </CardContent>
      </Card>
    </button>
  );
}

export default ThemeCard;
